import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  Newspaper,
  X,
  AlertTriangle,
  Anchor,
  Cloud,
  Train,
  Shield,
  Ship,
  Info,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  ExternalLink,
} from "lucide-react";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";


const CATEGORY_CONFIG = {
  port_update: { icon: Anchor, color: "bg-blue-500", label: "Port Update" },
  weather: { icon: Cloud, color: "bg-orange-500", label: "Weather Alert" },
  rail: { icon: Train, color: "bg-purple-500", label: "Rail Disruption" },
  customs: { icon: Shield, color: "bg-green-500", label: "Customs Notice" },
  shipping_line: { icon: Ship, color: "bg-indigo-500", label: "Shipping Line" },
  general: { icon: Info, color: "bg-gray-500", label: "General" },
};

const PRIORITY_CONFIG = {
  urgent: { color: "bg-red-600", textColor: "text-red-600", label: "Urgent" },
  high: { color: "bg-orange-500", textColor: "text-orange-500", label: "High Priority" },
  normal: { color: "bg-blue-500", textColor: "text-blue-500", label: "Normal" },
  low: { color: "bg-gray-400", textColor: "text-gray-400", label: "Low" },
};

// Pages where news popup should NOT appear (public pages)
const PUBLIC_PAGES = ["/", "/login", "/register", "/track"];

export const NewsPopup = () => {
  const { token, user, isAuthenticated, getAuthHeaders } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  // Check if we're on a public page
  const isPublicPage = PUBLIC_PAGES.some(page => 
    location.pathname === page || location.pathname.startsWith("/track/")
  );

  useEffect(() => {
    // Only check for news if user is truly authenticated (has user object) AND not on public page.
    // Skip for external partner accounts — their portal is single-purpose (documents only).
    if (isAuthenticated && user && token && !isPublicPage && user?.user_type !== "partner") {
      checkForUnreadNews();
    }
  }, [isAuthenticated, user, token, isPublicPage]);

  const checkForUnreadNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/news/latest`, {
        headers: getAuthHeaders(),
      });

      if (response.data.has_unread && response.data.news.length > 0) {
        setNewsItems(response.data.news);
        setIsOpen(true);
      }
    } catch (error) {
      console.error("Failed to check for news:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      setDismissing(true);
      await axios.post(`${API_URL}/news/dismiss`, {}, {
        headers: getAuthHeaders(),
      });
      setIsOpen(false);
      setNewsItems([]);
    } catch (error) {
      console.error("Failed to dismiss news:", error);
    } finally {
      setDismissing(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < newsItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Don't show on public pages or if no news/not authenticated
  if (isPublicPage || !isOpen || newsItems.length === 0 || !user) return null;

  const currentNews = newsItems[currentIndex];
  const categoryConfig = CATEGORY_CONFIG[currentNews.category] || CATEGORY_CONFIG.general;
  const priorityConfig = PRIORITY_CONFIG[currentNews.priority] || PRIORITY_CONFIG.normal;
  const CategoryIcon = categoryConfig.icon;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden p-0 gap-0"
        hideCloseButton
        data-testid="news-popup"
      >
        {/* Header with gradient based on category */}
        <div className={`relative ${categoryConfig.color} p-6 text-white`}>
          {/* Priority badge */}
          {currentNews.priority !== "normal" && (
            <div className="absolute top-4 right-4">
              <span className={`px-3 py-1 rounded-full text-xs font-bold bg-white/20 backdrop-blur-sm flex items-center gap-1`}>
                {currentNews.priority === "urgent" && <AlertTriangle className="h-3 w-3" />}
                {priorityConfig.label}
              </span>
            </div>
          )}

          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <CategoryIcon className="h-8 w-8" />
            </div>
            <div className="flex-1">
              <span className="text-xs font-medium opacity-80 uppercase tracking-wide">
                {categoryConfig.label}
              </span>
              <DialogTitle className="text-xl font-bold text-white mt-1 leading-tight">
                {currentNews.title}
              </DialogTitle>
            </div>
          </div>

          {/* Pagination indicator */}
          {newsItems.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              {newsItems.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === currentIndex ? "w-8 bg-white" : "w-2 bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[40vh]">
          {/* Source info */}
          {(currentNews.source_sender || currentNews.source_date) && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4 pb-4 border-b">
              {currentNews.source_sender && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{currentNews.source_sender}</span>
                </div>
              )}
              {currentNews.source_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(currentNews.source_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          )}

          {/* News content - formatted like a social media post */}
          <div className="prose prose-sm max-w-none">
            {/* Images - only show if valid base64 data or proper URL */}
            {currentNews.images && currentNews.images.length > 0 && currentNews.images.some(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http'))) && (
              <div className="mb-4 grid gap-2" style={{ gridTemplateColumns: currentNews.images.filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http'))).length === 1 ? '1fr' : 'repeat(2, 1fr)' }}>
                {currentNews.images
                  .filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http')))
                  .map((img, idx) => (
                  <img 
                    key={idx}
                    src={img.data} 
                    alt={img.filename || `Image ${idx + 1}`}
                    className="rounded-lg border shadow-sm w-full object-contain max-h-64 bg-muted"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ))}
              </div>
            )}
            
            {/* News body - render HTML if available, else plain text */}
            {currentNews.html_content ? (
              <div 
                className="prose prose-sm max-w-none text-foreground leading-relaxed news-html-content"
                dangerouslySetInnerHTML={{ __html: currentNews.html_content }}
                style={{ wordBreak: 'break-word' }}
              />
            ) : (
              <div className="whitespace-pre-wrap text-foreground leading-relaxed">
                {/* Parse content and make URLs clickable */}
                {currentNews.content?.split(/(https?:\/\/[^\s]+)/g).map((part, idx) => {
                  if (part.match(/^https?:\/\//)) {
                    return (
                      <a 
                        key={idx}
                        href={part} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {part}
                      </a>
                    );
                  }
                  return <span key={idx}>{part}</span>;
                })}
              </div>
            )}
          </div>
          
          {/* Source URL button */}
          {currentNews.source_url && (
            <div className="mt-4 pt-4 border-t">
              <a 
                href={currentNews.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                Read Full Article
              </a>
            </div>
          )}
        </div>

        {/* Footer with navigation and dismiss */}
        <div className="border-t p-4 bg-muted/30">
          <div className="flex items-center justify-between">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              {newsItems.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="gap-1"
                    data-testid="news-prev-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    {currentIndex + 1} of {newsItems.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNext}
                    disabled={currentIndex === newsItems.length - 1}
                    className="gap-1"
                    data-testid="news-next-btn"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>

            {/* Dismiss button */}
            <Button
              onClick={handleDismiss}
              disabled={dismissing}
              className="gap-2"
              data-testid="news-dismiss-btn"
            >
              {dismissing ? (
                <>Dismissing...</>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  {newsItems.length > 1 ? `Dismiss All (${newsItems.length})` : "Got it, continue"}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsPopup;
